import PropTypes from "prop-types";
import { useMemo } from "react";
import intl from "react-intl-universal";
import Button from "components/button";
import { SelectBox, Toggle, Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from "components/uikit-adapter";
import * as S from "../../styled";

const translateOrFallback = (key, fallback) => {
  if (!key) {
    return fallback;
  }

  const translated = intl.get(key);
  return translated || fallback;
};

export const FeatureFlagControl = ({ definition, flag, onToggle, isSaving }) => {
  const options = flag.options.map(option => ({
    ...option,
    label: option.value === "__DEFAULT__"
      ? `${intl.get("master-data.configurations.general-settings.feature-flags.default")} (${flag.DefaultValue})`
      : translateOrFallback(option.labelKey, option.label)
  }));

  if (definition.control === "toggle") {
    const selectedValue = flag.selection === "__DEFAULT__" ? flag.DefaultValue : flag.selection;
    const handleChange = target => {
      const nextValue = target.checked ? "True" : "False";
      const selection = nextValue.toLowerCase() === flag.DefaultValue.toLowerCase() ? "__DEFAULT__" : nextValue;
      onToggle(definition.parameter, flag.EccPlant, selection);
    };

    return (
      <Toggle
        label=""
        name={`feature-flag-${definition.parameter}-${flag.EccPlant}`}
        value={selectedValue.toLowerCase() === "true"}
        onChange={handleChange}
        disabled={isSaving}
        data-testid={`feature-flag-control-${definition.parameter}-${flag.EccPlant}`}
      />
    );
  }

  return (
    <SelectBox
      dontShowLabel
      label={`feature-flag-${definition.parameter}-${flag.EccPlant}`}
      name={`feature-flag-${definition.parameter}-${flag.EccPlant}`}
      options={options}
      labelKey="label"
      valueKey="value"
      value={options.find(option => option.value === flag.selection)}
      onChange={option => onToggle(definition.parameter, flag.EccPlant, option?.value || "__DEFAULT__")}
      clearable={false}
      disabled={isSaving}
      searchable={false}
      multi={false}
      placeholder={intl.get("master-data.configurations.general-settings.feature-flags.select")}
      data-testid={`feature-flag-control-${definition.parameter}-${flag.EccPlant}`}
    />
  );
};

const definitionShape = PropTypes.shape({
  parameter: PropTypes.string.isRequired,
  control: PropTypes.string.isRequired,
  title: PropTypes.string,
  titleKey: PropTypes.string,
  description: PropTypes.string,
  descriptionKey: PropTypes.string,
});

const flagShape = PropTypes.shape({
  BusinessUnitId: PropTypes.number,
  EccPlant: PropTypes.string.isRequired,
  Plant: PropTypes.string,
  Value: PropTypes.string.isRequired,
  DefaultValue: PropTypes.string.isRequired,
  Origin: PropTypes.string,
  selection: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string,
    labelKey: PropTypes.string,
  })).isRequired,
});

FeatureFlagControl.propTypes = {
  definition: definitionShape.isRequired,
  flag: flagShape.isRequired,
  onToggle: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

const FeatureFlagsSection = ({ sections, definitions, selectedFlags, selectedPlants, onFlagsChange, onPlantsChange, onToggle, onSave, isSaving }) => {
  const flagOptions = useMemo(() => definitions.map(definition => ({
    value: definition.parameter,
    label: translateOrFallback(definition.titleKey, definition.title)
  })), [definitions]);

  const plantOptions = useMemo(() => {
    const plants = sections.flatMap(section => section.flags).reduce((result, flag) => {
      if (!result.some(option => option.value === flag.EccPlant)) {
        result.push({ value: flag.EccPlant, label: flag.Plant });
      }
      return result;
    }, []);
    return plants.sort((left, right) => left.label.localeCompare(right.label));
  }, [sections]);

  const visibleSections = sections
    .filter(section => selectedFlags.length === 0 || selectedFlags.includes(section.definition.parameter))
    .map(section => ({
      ...section,
      flags: selectedPlants.length === 0
        ? section.flags
        : section.flags.filter(flag => selectedPlants.includes(flag.EccPlant))
    }));

  return (
    <S.FeatureFlagsSection data-testid="feature-flags-section">
      <S.FeatureFlagsHeader>
        <S.FeatureFlagsTitleGroup>
          <S.FeatureFlagsTitle>{intl.get("master-data.configurations.general-settings.feature-flags.multiTenantTitle")}</S.FeatureFlagsTitle>
          <S.FeatureFlagsSubtitle>{intl.get("master-data.configurations.general-settings.feature-flags.multiTenantSubtitle")}</S.FeatureFlagsSubtitle>
        </S.FeatureFlagsTitleGroup>
        <S.FeatureFlagsSummary>{visibleSections.reduce((count, section) => count + section.flags.length, 0)} {intl.get("master-data.configurations.general-settings.feature-flags.records")}</S.FeatureFlagsSummary>
      </S.FeatureFlagsHeader>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
        <SelectBox
          label={intl.get("master-data.configurations.general-settings.feature-flags.flagsLabel")}
          placeholder={intl.get("master-data.configurations.general-settings.feature-flags.flagsPlaceholder")}
          options={flagOptions}
          value={flagOptions.find(option => selectedFlags.includes(option.value)) || null}
          valueKey="value"
          labelKey="label"
          searchable
          clearable
          onChange={option => onFlagsChange(option ? [option.value] : [])}
          name="feature-flags-filter"
        />
        <SelectBox
          label={intl.get("master-data.configurations.general-settings.feature-flags.plantsLabel")}
          placeholder={intl.get("master-data.configurations.general-settings.feature-flags.plantsPlaceholder")}
          options={plantOptions}
          value={plantOptions.filter(option => selectedPlants.includes(option.value))}
          valueKey="value"
          labelKey="label"
          multi
          searchable
          clearable
          onChange={options => onPlantsChange((options || []).map(option => option.value))}
          name="feature-plants-filter"
        />
      </div>

      {visibleSections.map(section => (
        <div key={section.definition.parameter} data-testid={`feature-flag-group-${section.definition.parameter}`} style={{ marginTop: 20 }}>
          <h5 style={{ margin: "0 0 4px", color: "var(--color-contrast-brand)" }}>{translateOrFallback(section.definition.titleKey, section.definition.title)}</h5>
          <p style={{ margin: "0 0 8px", color: "var(--color-neutral-500)", fontSize: 12 }}>{translateOrFallback(section.definition.descriptionKey, section.definition.description)}</p>
          <Table selectable={false} multiSelectable={false}>
            <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
              <TableRow>
                <TableHeaderColumn>{intl.get("master-data.configurations.general-settings.feature-flags.plant")}</TableHeaderColumn>
                <TableHeaderColumn>{intl.get("master-data.configurations.general-settings.feature-flags.effectiveValue")}</TableHeaderColumn>
                <TableHeaderColumn>{intl.get("master-data.configurations.general-settings.feature-flags.origin")}</TableHeaderColumn>
                <TableHeaderColumn>{intl.get("master-data.configurations.general-settings.feature-flags.configuration")}</TableHeaderColumn>
              </TableRow>
            </TableHeader>
            <TableBody displayRowCheckbox={false}>
              {section.flags.map(flag => (
                <TableRow key={`${section.definition.parameter}-${flag.BusinessUnitId}`} data-testid={`feature-flag-row-${section.definition.parameter}-${flag.EccPlant}`}>
                  <TableRowColumn><S.FeatureFlagsName><strong>{flag.Plant}</strong><S.FeatureFlagsKey>{flag.BusinessUnitId}</S.FeatureFlagsKey></S.FeatureFlagsName></TableRowColumn>
                  <TableRowColumn>{flag.Value}</TableRowColumn>
                  <TableRowColumn><S.FeatureFlagsStatus $enabled={flag.Origin === "Override"}>{intl.get(`master-data.configurations.general-settings.feature-flags.${flag.Origin === "Override" ? "overrideOrigin" : "defaultOrigin"}`)}</S.FeatureFlagsStatus></TableRowColumn>
                  <TableRowColumn><FeatureFlagControl definition={section.definition} flag={flag} onToggle={onToggle} isSaving={isSaving} /></TableRowColumn>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}

      <S.FeatureFlagsFooter>
        <span className="helper-text">{intl.get("master-data.configurations.general-settings.feature-flags.saveMultiTenant")}</span>
        <Button buttonType="primary" value={intl.get("master-data.configurations.general-settings.feature-flags.save")} onClick={onSave} style={{ width: "160px" }} disabled={isSaving} data-testid="save-feature-flags-button" />
      </S.FeatureFlagsFooter>
    </S.FeatureFlagsSection>
  );
};

FeatureFlagsSection.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    definition: definitionShape.isRequired,
    flags: PropTypes.arrayOf(flagShape).isRequired,
  })).isRequired,
  definitions: PropTypes.arrayOf(definitionShape).isRequired,
  selectedFlags: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedPlants: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFlagsChange: PropTypes.func.isRequired,
  onPlantsChange: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

export default FeatureFlagsSection;
